import React, { useEffect, useState } from 'react';
import SiteFooter from '../components/SiteFooter';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, Send, CheckCircle, Users } from 'lucide-react';
import { apiFetch } from '../lib/api';
import LanguageSwitcher from '../components/LanguageSwitcher';

interface TestimonialData {
  displayName: string;
  testimonial: string;
  directRefs: number;
  publicApproved: number;
}

const TestimonialPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<TestimonialData | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [testimonial, setTestimonial] = useState('');

  useEffect(() => {
    if (!token) {
      setError(t('content:testimonial.incompleteLink'));
      setLoading(false);
      return;
    }

    apiFetch(`/api/public/testimonial?t=${encodeURIComponent(token)}`)
      .then((res) => res.json())
      .then((json) => {
        if (json.error) {
          setError(json.error);
        } else {
          setData(json);
          setDisplayName(json.displayName || t('content:testimonial.defaultDisplayName'));
          setTestimonial(json.testimonial || '');
        }
      })
      .catch((e) => {
        console.error(e);
        setError(t('content:testimonial.loadFailed'));
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testimonial.trim()) {
      setError(t('content:testimonial.emptyTestimonial'));
      return;
    }
    setSubmitting(true);
    setError('');

    try {
      const res = await apiFetch('/api/public/testimonial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, displayName: displayName.trim(), testimonial: testimonial.trim() }),
      });
      const json = await res.json();
      if (json.error) {
        setError(json.error);
      } else {
        setSubmitted(true);
      }
    } catch (e) {
      console.error(e);
      setError(t('content:testimonial.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF7ED] via-[#F2EEE6] to-[#EBE5DB] flex flex-col">
      <header className="bg-[#C2410C] text-white py-5 px-4 shadow-md">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Heart size={22} />
          </div>
          <h1 className="text-xl md:text-2xl font-bold">{t('content:testimonial.brand')}</h1>
          <div className="ml-auto">
            <LanguageSwitcher size="sm" />
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4 md:p-8">
        <div className="max-w-xl w-full bg-white rounded-[2rem] shadow-2xl border-2 border-orange-100 p-8 md:p-12">
          {loading ? (
            <div className="text-center py-12 text-stone-500">{t('content:testimonial.loading')}</div>
          ) : error && !data ? (
            <div className="text-center py-12 text-red-600">{error}</div>
          ) : submitted ? (
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <CheckCircle size={64} className="text-green-600" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-stone-800">{t('content:testimonial.submittedTitle')}</h2>
              <p className="text-stone-600 text-lg leading-relaxed">
                {t('content:testimonial.submittedBody')}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="text-center space-y-2">
                <p className="text-base md:text-lg font-bold text-[#C2410C] tracking-wider">{t('content:testimonial.badge')}</p>
                <h2 className="text-3xl md:text-4xl font-bold text-stone-800 leading-tight">
                  {t('content:testimonial.formTitle')}
                </h2>
                <p className="text-stone-600 text-lg">
                  {t('content:testimonial.influencedPrefix')} <span className="font-bold text-[#C2410C]">{data?.directRefs || 0}</span> {t('content:testimonial.influencedSuffix', { count: data?.directRefs || 0 })}
                </p>
              </div>

              <div className="bg-orange-50 rounded-2xl p-5 border border-orange-100">
                <div className="flex items-center gap-2 text-[#C2410C] font-semibold mb-3">
                  <Users size={20} />
                  <span>{t('content:testimonial.whyTitle')}</span>
                </div>
                <p className="text-stone-700 leading-relaxed">
                  {t('content:testimonial.whyBody')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-2">{t('content:testimonial.nameLabel')}</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={t('content:testimonial.namePlaceholder')}
                  maxLength={30}
                  className="w-full rounded-xl border border-stone-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C2410C]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-2">{t('content:testimonial.textLabel')}</label>
                <textarea
                  value={testimonial}
                  onChange={(e) => setTestimonial(e.target.value)}
                  placeholder={t('content:testimonial.textPlaceholder')}
                  maxLength={500}
                  rows={5}
                  className="w-full rounded-xl border border-stone-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C2410C] resize-none"
                />
                <p className="text-right text-xs text-stone-400 mt-1">{testimonial.length}/500</p>
              </div>

              {error && <div className="text-red-600 text-sm">{error}</div>}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#C2410C] hover:bg-[#9A3412] text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition disabled:opacity-60"
              >
                {submitting ? t('content:testimonial.submitting') : t('content:testimonial.submitBtn')}
                <Send size={18} />
              </button>

              <p className="text-center text-sm text-stone-400">
                {t('content:testimonial.reviewNote')}
              </p>
            </form>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default TestimonialPage;
